'use client';

import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAppDispatch, store } from '@/store/store';
import { workflowApi } from '@/store/api/workflowApi';
import workspaceApi from '@/store/api/workspaceApi';
import { addColumn } from '@/store/slices/workspaceColumnsSlice';
import {
  emailBulkStarted,
  emailBulkTick,
  emailBulkCleared,
  type WorkspaceEmailState,
} from '@/store/slices/workspaceEmailSlice';
import type { WorkspaceRow } from '@/lib/types/workspace.types';

/**
 * Trailing-edge debounce collapsing one batch's finishes into one rows call.
 * A bulk run emits one `workflow.finished` per row within seconds of each
 * other; refreshing the sheet's rows on every one would refetch the page N
 * times for what is a single job.
 */
const BULK_ROWS_DEBOUNCE_MS = 2500;

/** The `workflow.progress` payload fields this handler reads. */
export interface BulkWorkflowEvent {
  workflowId?: unknown;
  event?: unknown;
  category?: unknown;
  bulkBatchId?: unknown;
}

/**
 * Handles Workspace bulk-email SSE events (`POST /workspace/sendBulkEmail`
 * fans out server-side, so every event of the batch carries `bulkBatchId`).
 *
 * Returns true when the event belonged to a bulk batch — the caller then
 * skips the per-workflow path (feeding the slices + firing the GET resync
 * would be one network call per row for data nothing reads). Two things
 * happen instead: every finish ticks the batch's header progress, and the
 * sheet gets a single debounced rows refresh per batch. Batches started
 * outside this session (another tab, a curl, a pre-reload run) are adopted
 * once per batch via the workflow snapshot — toast + column + refresh.
 */
export function useWorkspaceBulkEvents() {
  const dispatch = useAppDispatch();
  const bulkRowsTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const unknownBulkBatches = useRef(new Map<string, 'resolving' | 'unresolvable'>());

  useEffect(() => {
    const timers = bulkRowsTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const scheduleBulkRowsRefetch = useCallback(
    (userUploadId: string, bulkBatchId: string) => {
      const timers = bulkRowsTimers.current;
      const prev = timers.get(bulkBatchId);
      if (prev) clearTimeout(prev);
      timers.set(
        bulkBatchId,
        setTimeout(() => {
          timers.delete(bulkBatchId);
          dispatch(workspaceApi.util.invalidateTags([{ type: 'WorkspaceRows', id: userUploadId }]));
          // Values are landing via that refresh, so the run stops occupying
          // the column header even if its total was never known.
          dispatch(emailBulkCleared(bulkBatchId));
        }, BULK_ROWS_DEBOUNCE_MS)
      );
    },
    [dispatch]
  );

  const resolveExternalBulkBatch = useCallback(
    (bulkBatchId: string, workflowId: string) => {
      const seen = unknownBulkBatches.current;
      if (seen.has(bulkBatchId)) return;
      seen.set(bulkBatchId, 'resolving');
      void dispatch(workflowApi.endpoints.getWorkflow.initiate(workflowId))
        .unwrap()
        .then((snapshot) => {
          const row =
            typeof snapshot.docId === 'string'
              ? (store.getState() as { workspaceData: { byId: Record<string, WorkspaceRow> } })
                  .workspaceData.byId[snapshot.docId]
              : undefined;
          if (typeof snapshot.columnName !== 'string' || !snapshot.columnName || !row) {
            // Not on this sheet's loaded page (or not a workspace workflow) —
            // leave it; opening the sheet later picks values up via rows.
            seen.set(bulkBatchId, 'unresolvable');
            return;
          }
          const { userupload_id: userUploadId } = row;
          const columnName = snapshot.columnName;
          dispatch(emailBulkStarted({ bulkBatchId, userUploadId, columnName }));
          // Idempotent (returns early if the key exists) and lifts removedKeys,
          // so re-runs and previously-deleted columns are safe.
          dispatch(addColumn({ key: columnName, name: columnName, type: 'email' }));
          toast.success(
            `Email enrichment started for “${columnName}” — values appear as rows are processed`
          );
          scheduleBulkRowsRefetch(userUploadId, bulkBatchId);
        })
        .catch(() => {
          seen.set(bulkBatchId, 'unresolvable');
        });
    },
    [dispatch, scheduleBulkRowsRefetch]
  );

  return useCallback(
    (p: BulkWorkflowEvent): boolean => {
      if (p.category !== 'email' || typeof p.bulkBatchId !== 'string' || !p.bulkBatchId) {
        return false;
      }
      // Count every finish toward the header progress first — works for
      // mapped and not-yet-mapped batches alike.
      if (p.event === 'workflow.finished') dispatch(emailBulkTick(p.bulkBatchId));
      const bulkRun = (store.getState() as { workspaceEmail: WorkspaceEmailState })
        .workspaceEmail?.bulkRunsByBatchId?.[p.bulkBatchId];
      if (bulkRun && bulkRun.userUploadId) {
        if (p.event === 'workflow.finished') scheduleBulkRowsRefetch(bulkRun.userUploadId, p.bulkBatchId);
      } else if (typeof p.workflowId === 'string' && p.workflowId) {
        // No mapping — the run started outside this session. Resolve it
        // once per batch (toast + column + refresh) instead of per row.
        resolveExternalBulkBatch(p.bulkBatchId, p.workflowId);
      }
      return true;
    },
    [dispatch, resolveExternalBulkBatch, scheduleBulkRowsRefetch]
  );
}
